/**
 * EditableScoreBadge — renders the AI match score and a companion manual
 * recruiter score chip that can be edited inline with one click.
 *
 * Rationale (B11): AI scoring is directional, not gospel. Every recruiter
 * has calibrated intuition the AI misses. This component lets them override
 * the score with a single click without leaving the current view — the
 * manual value persists (`applications.recruiter_score`) and always
 * displays alongside the AI score so both stakeholders can see the delta.
 *
 * Props:
 *   applicationId   (required) — target application id
 *   aiScore         AI-computed score (0-100)
 *   manualScore     current recruiter override (0-100 or null)
 *   size            "sm" | "md" — same as ScoreBadge
 *   processing      whether AI scoring is in flight
 *   status          "ai_paused" | null
 *   readOnly        hide the manual chip's edit affordance
 *   onChange        (newManualScore | null) => void — notify parent for
 *                   optimistic list-row updates
 */
import { useState } from "react";
import ScoreBadge from "@/components/ScoreBadge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, X, Check } from "lucide-react";

export default function EditableScoreBadge({
    applicationId,
    aiScore,
    manualScore,
    size = "md",
    processing = false,
    status = null,
    readOnly = false,
    onChange,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(manualScore ?? "");
    const [saving, setSaving] = useState(false);

    const commit = async () => {
        // Blank/null clears the override → fall back to AI score.
        const raw = String(draft).trim();
        let next = null;
        if (raw !== "") {
            const n = parseInt(raw, 10);
            if (Number.isNaN(n) || n < 0 || n > 100) {
                toast.error("Score must be 0–100");
                return;
            }
            next = n;
        }
        setSaving(true);
        try {
            await api.patch(`/applications/${applicationId}`, { recruiter_score: next });
            onChange?.(next);
            setEditing(false);
            toast.success(next === null ? "Manual score cleared" : `Manual score set to ${next}`);
        } catch { toast.error("Couldn't save score"); }
        finally { setSaving(false); }
    };

    const cancel = () => {
        setDraft(manualScore ?? "");
        setEditing(false);
    };

    const chipDim = size === "lg" ? "h-9 text-sm px-2" : size === "sm" ? "h-6 text-[10px] px-1.5" : "h-7 text-xs px-1.5";
    const iconDim = size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3";

    return (
        <div className="inline-flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()} data-testid="editable-score">
            <ScoreBadge score={aiScore} size={size} processing={processing} status={status} />

            {editing ? (
                <div className="inline-flex items-center gap-1">
                    <input
                        type="number"
                        min={0}
                        max={100}
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commit();
                            if (e.key === "Escape") cancel();
                        }}
                        placeholder="0-100"
                        className={`${chipDim} w-14 border border-ikb font-mono text-center bg-white`}
                        data-testid="manual-score-input"
                    />
                    <button type="button" onClick={commit} disabled={saving}
                        className={`${chipDim} border border-emerald-500 text-emerald-800 hover:bg-emerald-50 inline-flex items-center disabled:opacity-50`}
                        data-testid="manual-score-save" aria-label="Save manual score">
                        <Check className={iconDim} />
                    </button>
                    <button type="button" onClick={cancel} disabled={saving}
                        className={`${chipDim} border border-border hover:border-foreground inline-flex items-center`}
                        data-testid="manual-score-cancel" aria-label="Cancel">
                        <X className={iconDim} />
                    </button>
                </div>
            ) : manualScore != null ? (
                <button
                    type="button"
                    onClick={() => !readOnly && setEditing(true)}
                    disabled={readOnly}
                    title={readOnly ? `Manual score ${manualScore}` : "Click to edit manual score"}
                    className={`${chipDim} inline-flex items-center gap-1 border border-violet-500 bg-violet-50 text-violet-800 font-mono font-bold ${readOnly ? "cursor-default" : "hover:bg-violet-100 cursor-pointer"}`}
                    data-testid="manual-score-chip"
                >
                    <span className="opacity-70 text-[9px] uppercase tracking-widest">M</span>
                    {manualScore}
                    {!readOnly && <Pencil className={iconDim} />}
                </button>
            ) : !readOnly ? (
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    title="Set a manual recruiter score to override the AI"
                    className={`${chipDim} inline-flex items-center gap-1 border border-dashed border-border text-muted-foreground hover:border-ikb hover:text-ikb font-mono uppercase tracking-widest`}
                    data-testid="manual-score-add"
                >
                    <Pencil className={iconDim} /> Set
                </button>
            ) : null}
        </div>
    );
}
